/*
 * Aviator uses the MAIN application's login session.
 *
 * Main application keys:
 *   tokenl
 *   userid
 *   dev_id
 *
 * The old Aviator accessToken/refreshToken storage is intentionally not
 * used for authentication.
 */

const KEYS = {
  accessToken: "accessToken",
  refreshToken: "refreshToken",
  user: "skyfly_user",
  wallet: "skyfly_wallet",
  deviceId: "skyfly_device_id",
};

export const getMainAppAuth = () => ({
  token: localStorage.getItem("tokenl"),
  userid: localStorage.getItem("userid"),
  devId: localStorage.getItem("dev_id"),
});

export const getDeviceId = () => {
  // Main application device id has priority.
  const mainDeviceId = localStorage.getItem("dev_id");

  if (mainDeviceId) {
    return mainDeviceId;
  }

  let id = localStorage.getItem(KEYS.deviceId);

  if (!id) {
    id =
      `${navigator.userAgent.slice(0, 20)}-${
        crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random()}`
      }`;

    localStorage.setItem(KEYS.deviceId, id);
  }

  return id;
};

/*
 * Compatibility helpers.
 * They are kept because some existing components import them.
 * Authentication itself still comes from tokenl.
 */
export const setAuth = ({ accessToken, refreshToken, user, wallet }) => {
  if (accessToken) {
    localStorage.setItem(KEYS.accessToken, accessToken);
  }

  if (refreshToken) {
    localStorage.setItem(KEYS.refreshToken, refreshToken);
  }

  if (user) {
    localStorage.setItem(KEYS.user, JSON.stringify(user));
  }

  if (wallet) {
    localStorage.setItem(KEYS.wallet, JSON.stringify(wallet));
  }
};

export const getAccessToken = () =>
  localStorage.getItem("tokenl") ||
  localStorage.getItem(KEYS.accessToken);

export const getRefreshToken = () =>
  localStorage.getItem(KEYS.refreshToken);

export const getStoredUser = () => {
  try {
    const stored = JSON.parse(
      localStorage.getItem(KEYS.user) || "null"
    );

    /*
     * When Aviator is opened directly from the main application,
     * skyfly_user does not exist. Build the minimum user object from
     * the main application's userid.
     */
    const userid = localStorage.getItem("userid");

    if (stored) {
      return {
        ...stored,
        _id: stored._id || stored.id || stored.userId || userid,
        id: stored.id || stored.userId || stored._id || userid,
        userId: stored.userId || stored.id || stored._id || userid,
      };
    }

    if (userid) {
      return {
        _id: userid,
        id: userid,
        userId: userid,
      };
    }

    return null;
  } catch {
    const userid = localStorage.getItem("userid");

    return userid
      ? {
          _id: userid,
          id: userid,
          userId: userid,
        }
      : null;
  }
};

export const getStoredWallet = () => {
  try {
    return JSON.parse(
      localStorage.getItem(KEYS.wallet) || "null"
    );
  } catch {
    return null;
  }
};

/*
 * IMPORTANT:
 * Never delete tokenl / userid / dev_id here.
 * They belong to the MAIN application.
 */
export const clearAuth = () => {
  [
    KEYS.accessToken,
    KEYS.refreshToken,
    KEYS.user,
    KEYS.wallet,
  ].forEach((key) => localStorage.removeItem(key));
};
